import type { TaskCounters, TaskFilters, TaskPriority } from '../api';

/**
 * Role presets of the lists (Bitrix24 "Делаю / Помогаю / Поручил / Наблюдаю / Руковожу / Все"). `managed` — what the people of the
 * user's departments do — is offered to department heads only (the toolbar hides it when `counters.managedPeople` is 0).
 */
export type TaskPreset = 'doing' | 'assisting' | 'created' | 'following' | 'managed' | 'all';

export const PRESETS: readonly TaskPreset[] = [
  'doing',
  'assisting',
  'created',
  'following',
  'managed',
  'all',
];

export const PRESET_ROLE: Record<TaskPreset, NonNullable<TaskFilters['role']>> = {
  doing: 'responsible',
  assisting: 'accomplice',
  created: 'creator',
  following: 'auditor',
  managed: 'managed',
  all: 'all',
};

/** Counter shown next to a preset. */
export const PRESET_COUNTER: Partial<Record<TaskPreset, keyof TaskCounters>> = {
  doing: 'doing',
  assisting: 'assisting',
  created: 'created',
  following: 'following',
  managed: 'managed',
};

export type StatusFilter = 'active' | 'open' | 'completed' | 'deferred' | 'all';
export const STATUS_FILTERS: readonly StatusFilter[] = ['active', 'deferred', 'completed', 'all'];

export type DeadlineFilter = 'overdue' | 'today' | 'week' | 'none';
export const DEADLINE_FILTERS: readonly DeadlineFilter[] = ['overdue', 'today', 'week', 'none'];

/** The date ranges of the filters: days (`yyyy-MM-dd`, the user's own calendar), inclusive at both ends. */
export const DATE_KEYS = [
  'deadlineFrom',
  'deadlineTo',
  'createdFrom',
  'createdTo',
  'closedFrom',
  'closedTo',
] as const;
export type DateKey = (typeof DATE_KEYS)[number];

/** What the toolbar edits; serialized into the page URL so views can be shared and survive reloads. */
export interface ListState {
  preset: TaskPreset;
  status: StatusFilter;
  search: string;
  priority: TaskPriority | null;
  deadline: DeadlineFilter | null;
  groupId: string | null;
  tag: string | null;
  sort: string | null;
  /** «Руковожу»: one of the people the user manages (everyone when absent). */
  person: string | null;
  /** Filters by people (user ids): the responsible person, the creator, a co-executor, a watcher. */
  responsible: string | null;
  creator: string | null;
  accomplice: string | null;
  auditor: string | null;
  /** Tasks whose responsible person has left (is deactivated). */
  noResponsible: boolean;
  deadlineFrom: string | null;
  deadlineTo: string | null;
  createdFrom: string | null;
  createdTo: string | null;
  closedFrom: string | null;
  closedTo: string | null;
  page: number;
}

export const DEFAULT_LIST_STATE: ListState = {
  preset: 'doing',
  status: 'active',
  search: '',
  priority: null,
  deadline: null,
  groupId: null,
  tag: null,
  sort: null,
  person: null,
  responsible: null,
  creator: null,
  accomplice: null,
  auditor: null,
  noResponsible: false,
  deadlineFrom: null,
  deadlineTo: null,
  createdFrom: null,
  createdTo: null,
  closedFrom: null,
  closedTo: null,
  page: 1,
};

/** The people filters of the popover: state field → URL parameter → query parameter of the API. */
export const PEOPLE_FILTERS = [
  { field: 'creator', param: 'creator', query: 'creatorId' },
  { field: 'responsible', param: 'responsible', query: 'responsibleId' },
  { field: 'accomplice', param: 'accomplice', query: 'accompliceId' },
  { field: 'auditor', param: 'auditor', query: 'auditorId' },
] as const;

/**
 * The parameters of the page's address that make up a list state, in the order the server lists them for saved filters
 * (`Domain/SavedFilters.cs`): a saved filter stores exactly these as strings.
 */
export const SAVED_FILTER_KEYS = [
  'preset',
  'status',
  'q',
  'priority',
  'deadline',
  'group',
  'tag',
  'sort',
  'person',
  'responsible',
  'creator',
  'accomplice',
  'auditor',
  'noResponsible',
  ...DATE_KEYS,
] as const;

const oneOf = <T extends string>(value: string | null, allowed: readonly T[]): T | null =>
  value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : null;

const DAY = /^\d{4}-\d{2}-\d{2}$/u;
const day = (value: string | null): string | null => (value !== null && DAY.test(value) ? value : null);

export function parseListState(params: URLSearchParams, defaults: Partial<ListState> = {}): ListState {
  const base = { ...DEFAULT_LIST_STATE, ...defaults };
  const page = Number(params.get('page'));
  return {
    preset: oneOf(params.get('preset'), PRESETS) ?? base.preset,
    status: oneOf(params.get('status'), [...STATUS_FILTERS, 'open'] as StatusFilter[]) ?? base.status,
    search: params.get('q') ?? base.search,
    priority: oneOf(params.get('priority'), ['normal', 'high'] as TaskPriority[]) ?? base.priority,
    deadline: oneOf(params.get('deadline'), DEADLINE_FILTERS) ?? base.deadline,
    groupId: params.get('group') ?? base.groupId,
    tag: params.get('tag') ?? base.tag,
    sort: params.get('sort') ?? base.sort,
    person: params.get('person') ?? base.person,
    responsible: params.get('responsible') ?? base.responsible,
    creator: params.get('creator') ?? base.creator,
    accomplice: params.get('accomplice') ?? base.accomplice,
    auditor: params.get('auditor') ?? base.auditor,
    noResponsible: params.has('noResponsible') ? params.get('noResponsible') === '1' : base.noResponsible,
    deadlineFrom: day(params.get('deadlineFrom')) ?? base.deadlineFrom,
    deadlineTo: day(params.get('deadlineTo')) ?? base.deadlineTo,
    createdFrom: day(params.get('createdFrom')) ?? base.createdFrom,
    createdTo: day(params.get('createdTo')) ?? base.createdTo,
    closedFrom: day(params.get('closedFrom')) ?? base.closedFrom,
    closedTo: day(params.get('closedTo')) ?? base.closedTo,
    page: Number.isInteger(page) && page > 0 ? page : base.page,
  };
}

/** Writes `state` into `params` (keeping unrelated parameters such as `panel`), omitting defaults. */
export function writeListState(
  params: URLSearchParams,
  state: ListState,
  defaults: Partial<ListState> = {},
): URLSearchParams {
  const base = { ...DEFAULT_LIST_STATE, ...defaults };
  const next = new URLSearchParams(params);
  const put = (key: string, value: string | null, fallback: string | null) => {
    if (value === null || value === '' || value === fallback) next.delete(key);
    else next.set(key, value);
  };
  put('preset', state.preset, base.preset);
  put('status', state.status, base.status);
  put('q', state.search.trim(), '');
  put('priority', state.priority, base.priority);
  put('deadline', state.deadline, base.deadline);
  put('group', state.groupId, base.groupId);
  put('tag', state.tag, base.tag);
  put('sort', state.sort, base.sort);
  put('person', state.person, base.person);
  put('responsible', state.responsible, base.responsible);
  put('creator', state.creator, base.creator);
  put('accomplice', state.accomplice, base.accomplice);
  put('auditor', state.auditor, base.auditor);
  put('noResponsible', state.noResponsible ? '1' : null, base.noResponsible ? '1' : null);
  for (const key of DATE_KEYS) put(key, state[key], base[key]);
  put('page', String(state.page), String(base.page));
  return next;
}

/** Number of filters besides the preset and the search (badge on the filter button). */
export const activeFilterCount = (state: ListState, defaults: Partial<ListState> = {}): number => {
  const base = { ...DEFAULT_LIST_STATE, ...defaults };
  return [
    state.status !== base.status,
    state.priority !== base.priority,
    state.deadline !== base.deadline,
    state.groupId !== base.groupId,
    state.tag !== base.tag,
    state.responsible !== base.responsible,
    state.creator !== base.creator,
    state.accomplice !== base.accomplice,
    state.auditor !== base.auditor,
    state.noResponsible !== base.noResponsible,
    state.deadlineFrom !== base.deadlineFrom || state.deadlineTo !== base.deadlineTo,
    state.createdFrom !== base.createdFrom || state.createdTo !== base.createdTo,
    state.closedFrom !== base.closedFrom || state.closedTo !== base.closedTo,
  ].filter(Boolean).length;
};

/** The first instant of a day (`yyyy-MM-dd`) in the user's own calendar. */
export function startOfDay(value: string): Date {
  const [year = 0, month = 1, dayOfMonth = 1] = value.split('-').map(Number);
  return new Date(year, month - 1, dayOfMonth);
}

/** The first instant of the day after `value`: the exclusive end of a range that includes the day (a calendar step, so a 23- or 25-hour day is right). */
export function startOfNextDay(value: string): Date {
  const next = startOfDay(value);
  next.setDate(next.getDate() + 1);
  return next;
}

/** Query of the API for the list state at `now` ("today"/"this week" are local days). */
export function toTaskFilters(state: ListState, now: Date, fixed: Partial<TaskFilters> = {}): TaskFilters {
  const filters: TaskFilters = {
    role: PRESET_ROLE[state.preset],
    status: state.status,
    search: state.search.trim() || undefined,
    priority: state.priority ?? undefined,
    groupId: state.groupId ?? undefined,
    tag: state.tag ?? undefined,
    sort: state.sort ?? undefined,
  };
  if (state.preset === 'managed' && state.person) filters.userId = state.person;
  for (const { field, query } of PEOPLE_FILTERS) {
    const value = state[field];
    if (value) filters[query] = value;
  }
  if (state.noResponsible) filters.noResponsible = true;
  if (state.deadline === 'overdue' || state.deadline === 'none') filters.deadline = state.deadline;
  if (state.deadline === 'today' || state.deadline === 'week') {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    if (state.deadline === 'today') end.setDate(end.getDate() + 1);
    else end.setDate(end.getDate() + (8 - (start.getDay() === 0 ? 7 : start.getDay())));
    filters.deadlineFrom = start.toISOString();
    filters.deadlineTo = end.toISOString();
  }
  // An explicit range wins over the "today" / "this week" preset (the popover keeps only one of them).
  if (state.deadlineFrom) filters.deadlineFrom = startOfDay(state.deadlineFrom).toISOString();
  if (state.deadlineTo) filters.deadlineTo = startOfNextDay(state.deadlineTo).toISOString();
  if (state.createdFrom) filters.createdFrom = startOfDay(state.createdFrom).toISOString();
  if (state.createdTo) filters.createdTo = startOfNextDay(state.createdTo).toISOString();
  if (state.closedFrom) filters.closedFrom = startOfDay(state.closedFrom).toISOString();
  if (state.closedTo) filters.closedTo = startOfNextDay(state.closedTo).toISOString();
  return { ...filters, ...fixed };
}

/** The list state as a saved filter keeps it: the non-default parameters of the address, as strings (paging excluded). */
export function toSavedQuery(state: ListState, defaults: Partial<ListState> = {}): Record<string, string> {
  const params = writeListState(new URLSearchParams(), { ...state, page: 1 }, defaults);
  params.delete('page');
  return Object.fromEntries(
    [...params.entries()].filter(([key]) => (SAVED_FILTER_KEYS as readonly string[]).includes(key)),
  );
}

/** The list state a saved filter stands for: what it names, the defaults for the rest (so applying it replaces the current filter). */
export function fromSavedQuery(
  query: Readonly<Record<string, string>>,
  defaults: Partial<ListState> = {},
): ListState {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if ((SAVED_FILTER_KEYS as readonly string[]).includes(key)) params.set(key, value);
  }
  return parseListState(params, defaults);
}

/** Whether the list state is the one a saved filter stands for (the menu marks the filter that is applied). */
export function matchesSavedQuery(
  state: ListState,
  query: Readonly<Record<string, string>>,
  defaults: Partial<ListState> = {},
): boolean {
  const mine = toSavedQuery(state, defaults);
  const theirs = toSavedQuery(fromSavedQuery(query, defaults), defaults);
  const keys = new Set([...Object.keys(mine), ...Object.keys(theirs)]);
  return [...keys].every((key) => mine[key] === theirs[key]);
}
